import {
  labelNarrowingOf,
  matchesLabelNarrowing,
  serializeLabelNarrowing,
  type LabelNarrowing,
} from '@/features/labels';
import {
  READER_WAITING_LABEL,
  matchesTaskQuery,
  type WorkspaceTaskRow,
} from '@/features/tasks';
import type { TaskView, UserSummary } from '@/lib/api/generated/model';
import { displayNameOf } from '@/shared/components/accountIdentity';

/*
  The Tasks page's list view (spec 112): everything the page can be narrowed
  and ordered by, what the address writes for it, and what each summary tile
  applies.

  Its own model rather than the shared `TaskNarrowing`, which the board and
  Overview also read: those two keep their single state filter (FR-007), and a
  second meaning for the same type is how three pages would drift.
*/

/** What a row can be filtered by — the facts the summary tiles count. */
export const TASK_LIST_STATES = [
  'running',
  'queued',
  'subtasks',
  'waiting',
  'failed',
  'blocked',
  'review',
  'attention',
  'to-start',
  'at-rest',
  'completed',
] as const;
export type TaskListState = (typeof TASK_LIST_STATES)[number];

/**
 * What needs a person, as states: the one definition the "Needs a person"
 * tile, its view, the urgency sort and the Overview's Your turn all use
 * (spec 152 FR-015).
 */
export const NEEDS_PERSON_STATES = [
  'waiting',
  'review',
  'attention',
  'failed',
  'blocked',
  'to-start',
] as const satisfies readonly TaskListState[];

/** Whether a row needs a person: in any of `NEEDS_PERSON_STATES`. */
export function needsPerson(row: WorkspaceTaskRow): boolean {
  return NEEDS_PERSON_STATES.some((state) => hasState(row, state));
}

export const SORT_KEYS = [
  'task',
  'state',
  'assignee',
  'created',
  'leadTime',
  'cost',
  'updated',
] as const;
export type SortKey = (typeof SORT_KEYS)[number];
export type SortDir = 'asc' | 'desc';

/**
 * Where a column starts when first pressed: names A–Z, state most urgent
 * first, figures and times largest or newest first (FR-011).
 */
export const SORT_NATURAL: Record<SortKey, SortDir> = {
  task: 'asc',
  state: 'asc',
  assignee: 'asc',
  created: 'desc',
  leadTime: 'desc',
  cost: 'desc',
  updated: 'desc',
};

export const RANGE_SPANS = ['24h', '7d', '30d'] as const;
export type RangeSpan = (typeof RANGE_SPANS)[number];
export const RANGE_FIELDS = ['created', 'updated', 'finished'] as const;
export type RangeField = (typeof RANGE_FIELDS)[number];

const SPAN_MS: Record<RangeSpan, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

export interface TaskListView {
  q?: string;
  labels: LabelNarrowing;
  /** Any of these (OR); empty means any state. */
  states: TaskListState[];
  /** Workflow keys, any of these (OR); empty means every workflow. */
  workflows: string[];
  /** `'me'`, `'none'` (unassigned) or a user id. */
  assignee?: string;
  /** `'me'` or a user id. */
  creator?: string;
  range?: { since: RangeSpan; on: RangeField };
  /** List hidden tasks too. */
  hidden: boolean;
  /** Absent: the default order, most recently changed first. */
  sort?: { key: SortKey; dir: SortDir };
}

/**
 * The address parameters a view is written as. Every member optional. A type
 * alias, not an interface, so it can be carried as a plain string record by
 * code that keeps the parameters without reading them.
 */
export type TaskListSearch = {
  q?: string;
  labels?: string;
  state?: string;
  workflow?: string;
  assignee?: string;
  creator?: string;
  since?: string;
  on?: string;
  hidden?: string;
  sort?: string;
  dir?: string;
};

function oneOf<T extends string>(
  allowed: readonly T[],
  value: string | undefined,
): T | undefined {
  return allowed.find((candidate) => candidate === value);
}

function list(value: string | undefined): string[] {
  return (value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * The view an address describes. Anything it does not recognise is dropped
 * rather than refused — a shared link outlives the vocabulary it was written
 * in (FR-006). A single `state` value is simply a list of one, so links from
 * before this spec keep working.
 */
export function listViewOf(search: TaskListSearch): TaskListView {
  const q = search.q?.trim();
  const since = oneOf(RANGE_SPANS, search.since);
  const key = oneOf(SORT_KEYS, search.sort);
  const states = list(search.state).filter((state): state is TaskListState =>
    (TASK_LIST_STATES as readonly string[]).includes(state),
  );
  return {
    ...(q ? { q } : {}),
    labels: labelNarrowingOf({ labels: search.labels }),
    states: [...new Set(states)],
    workflows: [...new Set(list(search.workflow))],
    ...(search.assignee ? { assignee: search.assignee } : {}),
    ...(search.creator ? { creator: search.creator } : {}),
    ...(since
      ? { range: { since, on: oneOf(RANGE_FIELDS, search.on) ?? 'created' } }
      : {}),
    hidden: search.hidden === '1',
    ...(key
      ? {
          sort: {
            key,
            dir:
              oneOf(['asc', 'desc'] as const, search.dir) ?? SORT_NATURAL[key],
          },
        }
      : {}),
  };
}

/**
 * The address for a view — the inverse of `listViewOf`. `undefined` drops a
 * parameter: an unfiltered page says so by carrying nothing, and a sort in its
 * natural direction carries no `dir`.
 */
export function searchOfListView(view: TaskListView): TaskListSearch {
  return {
    q: view.q?.trim() ? view.q.trim() : undefined,
    labels: serializeLabelNarrowing(view.labels),
    state: view.states.length > 0 ? view.states.join(',') : undefined,
    workflow: view.workflows.length > 0 ? view.workflows.join(',') : undefined,
    assignee: view.assignee,
    creator: view.creator,
    since: view.range?.since,
    on: view.range ? view.range.on : undefined,
    hidden: view.hidden ? '1' : undefined,
    sort: view.sort?.key,
    dir:
      view.sort && view.sort.dir !== SORT_NATURAL[view.sort.key]
        ? view.sort.dir
        : undefined,
  };
}

/** How many filters are in effect, for "Clear filters (n)". The order is not one. */
export function activeFilterCount(view: TaskListView): number {
  return [
    view.q,
    view.labels,
    view.states.length > 0,
    view.workflows.length > 0,
    view.assignee,
    view.creator,
    view.range,
    view.hidden,
  ].filter(Boolean).length;
}

/** Every filter off; the order stays (FR-005). */
export function clearFilters(view: TaskListView): TaskListView {
  return {
    labels: undefined,
    states: [],
    workflows: [],
    hidden: false,
    ...(view.sort ? { sort: view.sort } : {}),
  };
}

/**
 * Whether a row is in `state`. The tiles count with it and the Overview's
 * legend links to it, so each count and its list are one definition.
 */
export function hasState(row: WorkspaceTaskRow, state: TaskListState): boolean {
  switch (state) {
    case 'running':
      // An agent executing. A parent waiting on its subtasks holds no run
      // slot (spec 150 FR-003), so it is not here (spec 153 FR-018).
      return row.state === 'running' && !row.waitingOnSubtasks;
    case 'subtasks':
      // Waiting on subtasks and on nothing else. A parent with subtasks to
      // start is the person's turn, listed there (spec 153 R3).
      return row.waitingOnSubtasks && row.toStart === 0;
    case 'at-rest':
      // Nothing is happening and nobody is needed: queued runs included,
      // since nothing runs yet, as the Overview counts them.
      return row.state === 'at-rest' && !needsPerson(row);
    case 'waiting':
    case 'failed':
      return row.state === state;
    case 'queued':
      return row.queued !== null;
    case 'blocked':
      return row.blocked !== null;
    case 'review':
      // A parent's flag at an open join is its subtasks' need, counted there.
      return row.needsReview && !row.rolledUp;
    case 'attention':
      // The service's flag, or a stop read from the run as the Overview reads
      // it: the same set the flag describes (failed, blocked or stopped).
      return (row.needsAttention && !row.rolledUp) || row.stopped;
    case 'to-start':
      return row.toStart > 0;
    case 'completed':
      return row.finished;
  }
}

function rangeTime(row: WorkspaceTaskRow, on: RangeField): string | null {
  if (on === 'created') return row.createdAt;
  if (on === 'updated') return row.updatedAt;
  return row.finishedAt;
}

/* ---------------------------------------------------------------------------
   Words. One vocabulary for the filter menus and the chips that report them
   (spec 113 FR-004): the board's words for the states it shares, plain ones
   for the rest.
   --------------------------------------------------------------------------- */

export const STATE_LABEL: Record<TaskListState, string> = {
  running: 'In progress',
  queued: 'Queued',
  subtasks: 'Waiting on subtasks',
  waiting: 'Waiting on you',
  failed: 'Errors',
  blocked: 'Spend limit',
  review: 'Needs review',
  attention: 'Needs attention',
  'to-start': 'Subtasks to start',
  'at-rest': 'At rest',
  completed: 'Completed',
};

/**
 * `STATE_LABEL` in words true of the person reading them (spec 193): a reader
 * (`useCanWork()` false) cannot answer a gate, so it waits on a member.
 *
 * Built at call time, not at load: this module sits on the import cycle
 * through `@/features/tasks` (see `YourTurnSection`), and a value read from it
 * while modules load may not exist yet.
 */
export function stateLabels(canWork: boolean): Record<TaskListState, string> {
  return canWork
    ? STATE_LABEL
    : { ...STATE_LABEL, waiting: READER_WAITING_LABEL };
}

export const SPAN_LABEL: Record<RangeSpan, string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};

export const FIELD_LABEL: Record<RangeField, string> = {
  created: 'Created',
  updated: 'Updated',
  finished: 'Finished',
};

export function personName(person: UserSummary): string {
  return displayNameOf(person.display_name, person.email);
}

/**
 * The people a workspace's tasks name, for the assignee and creator filters
 * and the chips that report them (spec 113 R3).
 *
 * The member themself is left out of the two option lists — they are offered
 * as "me" instead, so they are not listed twice — but kept in `everyone`, so a
 * link naming them by id still reads as their name.
 */
export function peopleOfTasks(
  tasks: readonly TaskView[],
  me: string | undefined,
): {
  assignees: UserSummary[];
  creators: UserSummary[];
  everyone: UserSummary[];
} {
  const assignees = new Map<string, UserSummary>();
  const creators = new Map<string, UserSummary>();
  for (const task of tasks) {
    if (task.assignee) assignees.set(task.assignee.id, task.assignee);
    if (task.created_by) creators.set(task.created_by.id, task.created_by);
  }
  const sorted = (list: Iterable<UserSummary>) =>
    [...list].sort((a, b) => personName(a).localeCompare(personName(b)));
  const notMe = (person: UserSummary) => person.id !== me;
  return {
    assignees: sorted(assignees.values()).filter(notMe),
    creators: sorted(creators.values()).filter(notMe),
    everyone: sorted(new Map([...assignees, ...creators]).values()),
  };
}

/* ---------------------------------------------------------------------------
   Chips (spec 113 US1). One per active value, each carrying the view without
   that value — so removing a chip is `onViewChange(chip.without)` and the
   component knows nothing about any filter.
   --------------------------------------------------------------------------- */

export interface FilterChip {
  /** Stable across removals: `q`, `state:<s>`, `label:<id>`, `workflow:<key>`, … */
  id: string;
  /** What the chip says, e.g. "State: Errors", "Assigned to me". */
  label: string;
  /** The view with only this value removed. */
  without: TaskListView;
}

/** What the chips need to name values the address holds only as ids. */
export interface ChipContext {
  labels: readonly { id: string; name: string }[];
  workflows: readonly { key: string; name: string }[];
  people: readonly UserSummary[];
  /** `useCanWork()`; a reader's chips name a gate in a reader's words. */
  canWork?: boolean;
}

/**
 * The chips for a view. States follow the menu's order; labels and workflows
 * keep the address's. A value this workspace no longer has yields no chip —
 * it is already ignored when filtering, so a chip for it would report a filter
 * that does nothing. "Me" always yields one, even before the session is read:
 * the filter is set, whether or not it can narrow yet.
 */
export function filterChips(
  view: TaskListView,
  context: ChipContext,
): FilterChip[] {
  const chips: FilterChip[] = [];
  const stateLabel = stateLabels(context.canWork ?? true);
  const nameOfPerson = (id: string) => {
    const person = context.people.find((p) => p.id === id);
    return person ? personName(person) : undefined;
  };

  if (view.q) {
    chips.push({
      id: 'q',
      label: `Search: “${view.q}”`,
      without: { ...view, q: undefined },
    });
  }

  for (const state of TASK_LIST_STATES) {
    if (!view.states.includes(state)) continue;
    chips.push({
      id: `state:${state}`,
      label: `State: ${stateLabel[state]}`,
      without: { ...view, states: view.states.filter((s) => s !== state) },
    });
  }

  if (view.labels?.kind === 'unlabelled') {
    chips.push({
      id: 'unlabelled',
      label: 'Unlabelled',
      without: { ...view, labels: undefined },
    });
  } else if (view.labels?.kind === 'labels') {
    const ids = view.labels.ids;
    for (const id of ids) {
      const label = context.labels.find((l) => l.id === id);
      if (!label) continue;
      const rest = ids.filter((other) => other !== id);
      chips.push({
        id: `label:${id}`,
        label: `Label: ${label.name}`,
        without: {
          ...view,
          labels: rest.length > 0 ? { kind: 'labels', ids: rest } : undefined,
        },
      });
    }
  }

  for (const key of view.workflows) {
    const workflow = context.workflows.find((w) => w.key === key);
    if (!workflow) continue;
    chips.push({
      id: `workflow:${key}`,
      label: `Workflow: ${workflow.name}`,
      without: { ...view, workflows: view.workflows.filter((k) => k !== key) },
    });
  }

  if (view.assignee) {
    const label =
      view.assignee === 'me'
        ? 'Assigned to me'
        : view.assignee === 'none'
          ? 'Unassigned'
          : (() => {
              const name = nameOfPerson(view.assignee);
              return name ? `Assignee: ${name}` : undefined;
            })();
    if (label) {
      chips.push({
        id: 'assignee',
        label,
        without: { ...view, assignee: undefined },
      });
    }
  }

  if (view.creator) {
    const name = view.creator === 'me' ? undefined : nameOfPerson(view.creator);
    const label =
      view.creator === 'me'
        ? 'Created by me'
        : name
          ? `Created by: ${name}`
          : undefined;
    if (label) {
      chips.push({
        id: 'creator',
        label,
        without: { ...view, creator: undefined },
      });
    }
  }

  if (view.range) {
    chips.push({
      id: 'range',
      label: `${FIELD_LABEL[view.range.on]} · ${SPAN_LABEL[view.range.since].toLowerCase()}`,
      without: { ...view, range: undefined },
    });
  }

  if (view.hidden) {
    chips.push({
      id: 'hidden',
      label: 'Hidden tasks shown',
      without: { ...view, hidden: false },
    });
  }

  return chips;
}

/**
 * Whether a row belongs in the view. Filters combine with AND; the values of
 * one filter — several states, several workflows — with OR (FR-001, FR-004).
 *
 * `me` is the signed-in member's id. Until it is known, "me" filters nothing
 * rather than everything: an empty list while the session loads would read as
 * "you have no tasks".
 */
export function matchesListView(
  row: WorkspaceTaskRow,
  view: TaskListView,
  context: { me?: string; now: number },
): boolean {
  if (row.hidden && !view.hidden) return false;

  if (
    !matchesTaskQuery(
      {
        title: row.title,
        description: row.description,
        // The Tasks page shows more of a task than a board does, so more of it
        // is searchable (FR-009).
        also: [
          row.taskId,
          row.workflowName,
          row.stepLabel ?? '',
          row.createdBy?.display_name ?? '',
          row.createdBy?.email ?? '',
        ],
      },
      view.q,
    )
  ) {
    return false;
  }

  if (
    view.labels &&
    !matchesLabelNarrowing({ labels: row.labels }, view.labels)
  ) {
    return false;
  }
  if (view.states.length > 0 && !view.states.some((s) => hasState(row, s))) {
    return false;
  }
  if (view.workflows.length > 0 && !view.workflows.includes(row.workflowKey)) {
    return false;
  }

  if (view.assignee) {
    const target = view.assignee === 'me' ? context.me : view.assignee;
    if (view.assignee === 'none') {
      if (row.assignee !== null) return false;
    } else if (target !== undefined && row.assignee?.id !== target) {
      return false;
    }
  }
  if (view.creator) {
    const target = view.creator === 'me' ? context.me : view.creator;
    if (target !== undefined && row.createdBy?.id !== target) return false;
  }

  if (view.range) {
    const at = rangeTime(row, view.range.on);
    if (at === null) return false;
    if (Date.parse(at) < context.now - SPAN_MS[view.range.since]) return false;
  }

  return true;
}

/* ---------------------------------------------------------------------------
   Tiles. Each applies the view that lists exactly what it counts (FR-016):
   the same facts `summarizeTaskTiles` counts, and for Completed the same
   seven-day window on the time a task finished.
   --------------------------------------------------------------------------- */

export type TileKey =
  | 'runs-at-once'
  | 'running'
  | 'needs-person'
  | 'failed'
  | 'blocked'
  | 'completed';

const TILE_FILTER: Record<TileKey, Pick<TaskListView, 'states' | 'range'>> = {
  // Running and waiting together (spec 118 FR-010): the question "how busy is this
  // workspace" counts both. `running` alone is the tile without capacity figures.
  'runs-at-once': { states: ['running', 'queued'] },
  running: { states: ['running'] },
  'needs-person': { states: [...NEEDS_PERSON_STATES] },
  failed: { states: ['failed'] },
  blocked: { states: ['blocked'] },
  completed: { states: ['completed'], range: { since: '7d', on: 'finished' } },
};

/**
 * The view a tile applies. It **replaces** every filter and the search —
 * merging would list fewer than the tile counted — and keeps the order and
 * whether hidden tasks are shown, which are not part of what it counts.
 */
export function tileView(tile: TileKey, current: TaskListView): TaskListView {
  return {
    ...clearFilters(current),
    hidden: current.hidden,
    states: [...TILE_FILTER[tile].states],
    ...(TILE_FILTER[tile].range ? { range: TILE_FILTER[tile].range } : {}),
  };
}

/** Whether the filters in effect are exactly the ones a tile applies (FR-017). */
export function isTileView(view: TaskListView, tile: TileKey): boolean {
  const filter = TILE_FILTER[tile];
  const sameStates =
    view.states.length === filter.states.length &&
    filter.states.every((state) => view.states.includes(state));
  const sameRange =
    view.range?.since === filter.range?.since &&
    view.range?.on === filter.range?.on;
  return (
    sameStates &&
    sameRange &&
    !view.q &&
    !view.labels &&
    view.workflows.length === 0 &&
    !view.assignee &&
    !view.creator
  );
}

/* ---------------------------------------------------------------------------
   Built-in views (spec 114 US1): All, Mine, Needs me. Presets over the filters
   the address already carries — never a parameter of their own, so a shared
   link reproduces the selection because the selection is *recognised* from the
   filters, not stored beside them where the two could disagree (FR-004).
   --------------------------------------------------------------------------- */

export const BUILT_IN_VIEWS = ['all', 'mine', 'needs-me'] as const;
export type BuiltInView = (typeof BUILT_IN_VIEWS)[number];

const VIEW_FILTER: Record<
  BuiltInView,
  Pick<TaskListView, 'states' | 'assignee'>
> = {
  all: { states: [] },
  mine: { states: [], assignee: 'me' },
  'needs-me': { states: ['waiting', 'review', 'attention'], assignee: 'me' },
};

/**
 * The view a built-in view applies. Like a tile it **replaces** every filter
 * and the search, and keeps the order and whether hidden tasks are shown,
 * which are not part of what it asks (FR-002).
 */
export function builtInView(
  key: BuiltInView,
  current: TaskListView,
): TaskListView {
  const { states, assignee } = VIEW_FILTER[key];
  return {
    ...clearFilters(current),
    hidden: current.hidden,
    states: [...states],
    ...(assignee ? { assignee } : {}),
  };
}

/**
 * The built-in view whose filters and search are exactly the ones in effect,
 * if any (FR-003). The order of states, the list's order and hidden are not
 * part of a view, so they never stop one being recognised.
 */
export function activeBuiltInView(view: TaskListView): BuiltInView | undefined {
  if (
    view.q ||
    view.labels ||
    view.workflows.length > 0 ||
    view.creator ||
    view.range
  ) {
    return undefined;
  }
  return BUILT_IN_VIEWS.find((key) => {
    const filter = VIEW_FILTER[key];
    return (
      view.assignee === filter.assignee &&
      view.states.length === filter.states.length &&
      filter.states.every((state) => view.states.includes(state))
    );
  });
}
